"use client";

import * as Headless from "@headlessui/react";
import clsx from "clsx";
import React from "react";
import { Badge, badgeColors } from "src/components/base/badge";

export type BadgeColorKey = (typeof badgeColors)[number];

/**
 * Swatch bg classes for every badge color.
 * Adding or removing a color from badge.tsx without updating this map is a compile error.
 */
const SWATCH_CLASSES: Record<BadgeColorKey, string> = {
    red: "bg-red-500",
    orange: "bg-orange-500",
    amber: "bg-amber-400",
    yellow: "bg-yellow-400",
    lime: "bg-lime-400",
    green: "bg-green-500",
    emerald: "bg-emerald-500",
    teal: "bg-teal-500",
    cyan: "bg-cyan-400",
    sky: "bg-sky-500",
    blue: "bg-blue-500",
    indigo: "bg-indigo-500",
    violet: "bg-violet-500",
    purple: "bg-purple-500",
    fuchsia: "bg-fuchsia-400",
    pink: "bg-pink-400",
    rose: "bg-rose-400",
    zinc: "bg-zinc-400",
};

const SWATCH_ENTRIES = Object.entries(SWATCH_CLASSES) as [BadgeColorKey, string][];

/**
 * The dropdown options of a color picker: the full badge color palette as a grid of swatches.
 * Must be rendered inside a `Headless.Listbox` whose value is a {@link BadgeColorKey}.
 */
function ColorSwatchOptions() {
    return (
        <Headless.Transition
            leave="transition-opacity duration-100 ease-in pointer-events-none"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <Headless.ListboxOptions
                anchor="bottom start"
                className={clsx(
                    "[--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(2)]",
                    "isolate rounded-xl p-1.5 select-none",
                    "outline outline-1 outline-transparent focus:outline-hidden",
                    "bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75",
                    "shadow-lg ring-1 ring-zinc-950/10 dark:ring-white/10 dark:ring-inset",
                )}
            >
                <div className="grid grid-cols-6 gap-0.5">
                    {SWATCH_ENTRIES.map(([colorValue, swatchClass]) => (
                        <Headless.ListboxOption
                            key={colorValue}
                            value={colorValue}
                            title={colorValue}
                            className="group/option flex cursor-pointer items-center justify-center rounded-lg p-1 outline-hidden data-disabled:opacity-50 data-focus:bg-zinc-100 dark:data-focus:bg-zinc-700"
                        >
                            <span className={clsx("flex size-7 items-center justify-center rounded-full", swatchClass)}>
                                <svg
                                    className="hidden size-4 stroke-white group-data-selected/option:block"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M4 8.5l3 3L12 4"
                                        strokeWidth={1.5}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                        </Headless.ListboxOption>
                    ))}
                </div>
            </Headless.ListboxOptions>
        </Headless.Transition>
    );
}

export type ColorPickerProps<T extends BadgeColorKey> = {
    /** The currently selected value */
    value: T;
    /** Called when the user selects a different color */
    onChange: (value: T) => void;
    /** Whether the picker is disabled */
    disabled?: boolean;
    "aria-label"?: string;
    /**
     * When provided, the rendered node itself becomes the dropdown trigger (e.g. a live badge preview)
     * instead of the default input-styled button showing a color swatch and the color name.
     */
    renderValue?: (value: T) => React.ReactNode;
};

/**
 * A dropdown color picker showing the full badge color palette as a grid of swatches.
 * Styled to match the Listbox / Input components.
 *
 * `T` must extend `BadgeColorKey` — the value is always a valid badge color key,
 * so no conversion is needed for `Badge color={value}`.
 */
export default function ColorPicker<T extends BadgeColorKey>({
    value,
    onChange,
    disabled,
    "aria-label": ariaLabel,
    renderValue,
}: ColorPickerProps<T>) {
    return (
        <Headless.Listbox value={value} onChange={onChange} disabled={disabled}>
            {renderValue ? (
                <Headless.ListboxButton
                    data-slot="control"
                    aria-label={ariaLabel}
                    className={clsx(
                        "group inline-flex w-fit max-w-full cursor-pointer rounded-md",
                        "focus:outline-hidden data-focus:ring-2 data-focus:ring-blue-500",
                        "data-disabled:opacity-50",
                    )}
                >
                    {renderValue(value)}
                </Headless.ListboxButton>
            ) : (
                <Headless.ListboxButton
                    data-slot="control"
                    aria-label={ariaLabel}
                    className={clsx(
                        "group relative block w-full",
                        "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm dark:before:hidden",
                        "focus:outline-hidden",
                        "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset data-focus:after:ring-2 data-focus:after:ring-blue-500",
                        "data-disabled:opacity-50 data-disabled:before:bg-zinc-950/5 data-disabled:before:shadow-none",
                    )}
                >
                    <span
                        className={clsx(
                            "relative flex min-h-11 w-full items-center gap-2 rounded-lg",
                            "py-[calc(--spacing(2.5)-1px)] pr-[calc(--spacing(7)-1px)] pl-[calc(--spacing(3.5)-1px)]",
                            "sm:min-h-9 sm:py-[calc(--spacing(1.5)-1px)] sm:pl-[calc(--spacing(3)-1px)]",
                            "border border-zinc-950/10 bg-transparent",
                            "group-data-active:border-zinc-950/20 group-data-hover:border-zinc-950/20",
                            "dark:border-white/10 dark:bg-white/5",
                            "dark:group-data-active:border-white/20 dark:group-data-hover:border-white/20",
                            "group-data-disabled:border-zinc-950/20 dark:group-data-disabled:border-white/15",
                        )}
                    >
                        <span className={clsx("size-4 shrink-0 rounded-full", SWATCH_CLASSES[value])} />
                        <Badge color={value}>{value}</Badge>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <svg
                                className="size-5 stroke-zinc-500 group-data-disabled:stroke-zinc-600 sm:size-4 dark:stroke-zinc-400 forced-colors:stroke-[CanvasText]"
                                viewBox="0 0 16 16"
                                aria-hidden="true"
                                fill="none"
                            >
                                <path
                                    d="M5.75 10.75L8 13L10.25 10.75"
                                    strokeWidth={1.5}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M10.25 5.25L8 3L5.75 5.25"
                                    strokeWidth={1.5}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </span>
                </Headless.ListboxButton>
            )}

            <ColorSwatchOptions />
        </Headless.Listbox>
    );
}
